import { useState, useEffect, useMemo } from 'react';
import { fetchSalesData } from '../lib/salesData';
import { downloadXlsxSheets } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

const PERIODS = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
];

const REV_LABELS = {
  yesterday: "Yesterday's Revenue",
  '7d': 'Revenue (last 7 days)',
  '30d': 'Revenue (last 30 days)',
};

const FLAG_BY_COUNTRY = {
  Australia: '\u{1F1E6}\u{1F1FA}',
  'United States': '\u{1F1FA}\u{1F1F8}',
  'New Zealand': '\u{1F1F3}\u{1F1FF}',
  'United Kingdom': '\u{1F1EC}\u{1F1E7}',
};

function fmtMoney(n, currency = 'AUD') {
  if (n == null || Number.isNaN(n)) return '—';
  const symbol = currency === 'USD' ? '$' : currency === 'NZD' ? 'NZ$' : currency === 'GBP' ? '£' : 'A$';
  return `${symbol}${Math.round(n).toLocaleString()}`;
}

function fmtCount(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return Math.round(n).toLocaleString();
}

function fmtPct(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function fmtSignedCount(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${Math.round(n).toLocaleString()}`;
}

function fmtDelta(curr, prev) {
  if (!prev || prev === 0) return null;
  const pct = ((curr - prev) / prev) * 100;
  const sign = pct >= 0 ? '+' : '';
  return { text: `${sign}${pct.toFixed(1)}%`, up: pct >= 0 };
}

function fmtRelativeTime(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diffMs)) return '';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      // Might be a bare yyyy-MM-dd from Skio
      return iso;
    }
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
}

// Signal tag → chip styling.
// Enriched path: Claude returns signals like 'big_order' / 'loyal' / 'milestone'
// / 'reactivation' / 'new_to_brand' / 'international' / 'wholesale' / 'high_ltv'.
// Fallback path (SQL rules): only 'big_order' / 'repeat' / 'high_ltv'.
const SIGNAL_CHIP_STYLES = {
  big_order:    { label: 'Big order',     color: '#7c3aed', bg: '#ede9fe' },
  milestone:    { label: 'Milestone',     color: '#16a34a', bg: '#dcfce7' },
  loyal:        { label: 'Loyal',         color: '#16a34a', bg: '#dcfce7' },
  repeat:       { label: 'Repeat',        color: '#16a34a', bg: '#dcfce7' },
  high_ltv:     { label: 'High LTV',      color: '#d97706', bg: '#fef3c7' },
  reactivation: { label: 'Reactivated',   color: '#0891b2', bg: '#cffafe' },
  new_to_brand: { label: 'New-to-brand',  color: '#db2777', bg: '#fce7f3' },
  international:{ label: 'International', color: '#2563eb', bg: '#dbeafe' },
  wholesale:    { label: 'Wholesale',     color: '#1e40af', bg: '#dbeafe' },
};

function vipSignalChips(v) {
  // Support both `signals` (new) and `reasons` (legacy deploys in-flight).
  const tags = v.signals || v.reasons || [];
  return tags.map(tag => SIGNAL_CHIP_STYLES[tag] || { label: tag.replace(/_/g, ' '), color: '#52525b', bg: '#f4f4f5' });
}

function Skeleton({ height = 24, width = '100%' }) {
  return <div style={{ height, width, background: '#f4f4f5', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />;
}

export default function Sales({ session }) {
  // No finer-grained "sales_write" flag exists yet — admin is the closest
  // write-permission proxy for this read-only dashboard.
  const canWrite = session?.role === 'admin';
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSalesData(session?.code)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message || 'Failed to load sales data'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [session?.code, reloadKey]);

  const p = data?.periods?.[period];
  const skio = p?.skio;
  const revDelta = useMemo(() => p ? fmtDelta(p.revenue, p.revenue_prev) : null, [p]);
  const cancDelta = useMemo(() => skio ? fmtDelta(skio.cancellations, skio.cancellations_prev) : null, [skio]);
  // Top products scope to the selected period. Lambda returns three buckets
  // (yesterday / 7d / 30d) pre-sorted; frontend just picks one.
  const topProducts = useMemo(() => {
    const buckets = data?.top_products_by_period;
    return (buckets?.[period] || []).slice(0, 5);
  }, [data?.top_products_by_period, period]);
  const topTotalRevenue = useMemo(
    () => topProducts.reduce((s, x) => s + (x.revenue || 0), 0),
    [topProducts],
  );
  const currency = data?.vip_orders?.[0]?.currency || 'AUD';

  // Shared by the VIP Orders render block and the Excel export — same
  // period-scoped filter, one source of truth.
  const vips = useMemo(() => {
    const periodDays = period === 'yesterday' ? 1 : period === '7d' ? 7 : 30;
    const cutoff = Date.now() - periodDays * 24 * 60 * 60 * 1000;
    return (data?.vip_orders || []).filter(v => {
      const t = new Date(v.created_at).getTime();
      return !Number.isNaN(t) && t >= cutoff;
    });
  }, [data, period]);

  function handleDownload() {
    const topProductsColumns = [
      { label: 'Product', width: 30, value: r => r.title || '' },
      { label: 'SKU', width: 16, value: r => r.sku || '' },
      { label: 'Units', width: 10, numFmt: '#,##0', value: r => Math.round(r.units || 0) },
      { label: 'Revenue', width: 14, numFmt: `"${currency}"#,##0`, value: r => Math.round(r.revenue || 0) },
      { label: '% of Top', width: 10, numFmt: '0.0"%"', value: r => topTotalRevenue > 0 ? Number(((r.revenue / topTotalRevenue) * 100).toFixed(1)) : '' },
    ];
    const vipColumns = [
      { label: 'Order #', width: 14, value: r => r.order_number || '' },
      { label: 'Email', width: 26, value: r => r.customer_email || '' },
      { label: 'City', width: 16, value: r => r.shipping_city || '' },
      { label: 'Country', width: 16, value: r => r.shipping_country || '' },
      { label: 'Total', width: 12, numFmt: '#,##0', value: r => Math.round(r.total || 0) },
      { label: 'Currency', width: 10, value: r => r.currency || '' },
      { label: 'Lifetime Orders', width: 14, numFmt: '#,##0', value: r => r.lifetime_orders ?? '' },
      { label: 'Lifetime Spend', width: 14, numFmt: '#,##0', value: r => r.lifetime_spend != null ? Math.round(r.lifetime_spend) : '' },
      { label: 'Signals', width: 24, value: r => (r.signals || r.reasons || []).join(', ') },
      { label: 'Created At', width: 14, value: r => r.created_at ? r.created_at.slice(0, 10) : '' },
    ];
    const filename = `FON_Sales_${period}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsxSheets(filename, [
      { name: 'Top Products', columns: topProductsColumns, rows: topProducts },
      { name: 'VIP Orders', columns: vipColumns, rows: vips },
    ]);
    logExportActivity(session, 'Sales', filename);
  }

  return (
    <div className="app-screen" style={{ background: '#f7f8fa' }}>
      <div className="main-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: '#0a0a0a', margin: 0 }}>Sales</h1>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 2, background: '#f4f4f5', borderRadius: 8, padding: 2 }}>
              {PERIODS.map(pi => (
                <button key={pi.key} onClick={() => setPeriod(pi.key)} style={{
                  padding: '5px 10px', fontSize: 11, fontWeight: 500, borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: period === pi.key ? '#fff' : 'transparent', color: period === pi.key ? '#0a0a0a' : '#a1a1aa',
                  boxShadow: period === pi.key ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                }}>{pi.label}</button>
              ))}
            </div>
            <button
              onClick={() => setReloadKey(k => k + 1)}
              disabled={loading}
              style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e4e4e7', background: '#fff', cursor: loading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a1a1aa', opacity: loading ? 0.4 : 1 }}
              title="Refresh"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </button>
            {canWrite && <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>}
          </div>
        </div>

        {/* Freshness */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, minHeight: 20, flexWrap: 'wrap' }}>
          {data?.data_sources?.shopify?.orders_through && (
            <span title={`Latest Shopify order: ${data.data_sources.shopify.orders_through}`}
                  style={{ fontSize: 9, padding: '2px 8px', borderRadius: 999, background: '#dcfce7', color: '#16a34a', fontWeight: 500 }}>
              Shopify: latest order {fmtRelativeTime(data.data_sources.shopify.orders_through)}
            </span>
          )}
          {data?.data_sources?.skio?.through && (
            <span title={`Skio data through ${data.data_sources.skio.through}`}
                  style={{ fontSize: 9, padding: '2px 8px', borderRadius: 999, background: '#ede9fe', color: '#7c3aed', fontWeight: 500 }}>
              Skio: through {fmtDate(data.data_sources.skio.through)}
            </span>
          )}
          {data?.generated_at && (
            <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 999, background: '#f4f4f5', color: '#a1a1aa', fontWeight: 500 }}>
              Pulled {fmtRelativeTime(data.generated_at)}{data.cache_age_seconds > 0 && ` · cached ${Math.round(data.cache_age_seconds / 60)}m`}
            </span>
          )}
        </div>

        {/* Error banner */}
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>Couldn't load today's numbers</div>
              <div style={{ fontSize: 11, color: '#991b1b', marginTop: 2 }}>{error}</div>
            </div>
            <button onClick={() => setReloadKey(k => k + 1)} style={{ fontSize: 11, padding: '6px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}>Retry</button>
          </div>
        )}

        {/* KPIs — "Recurring Rev (30d)" is actual recurring revenue billed in
            the trailing 30 days (sum of skio_daily_revenue.recurring_revenue).
            Not a run-rate MRR — we don't have subscription frequency in the
            warehouse, so a faithful MRR isn't computable from fon_analytics
            alone. The number reflects real cash collected from subscribers. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: REV_LABELS[period], value: loading ? null : fmtMoney(p?.revenue, currency), delta: revDelta },
            { label: 'AOV', value: loading ? null : fmtMoney(p?.aov, currency), delta: null },
            { label: 'Active Subscribers', value: loading ? null : fmtCount(skio?.active), delta: skio ? { text: `${fmtSignedCount(skio.net_new)} net new`, up: (skio.net_new || 0) >= 0 } : null },
            { label: 'Recurring Rev (30d)', value: loading ? null : fmtMoney(skio?.mrr, currency), delta: skio ? { text: `${fmtPct(skio.churn_rate_pct)} churn (period)`, up: false, neutral: true } : null },
          ].map(k => (
            <div key={k.label} style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontSize: 10, fontWeight: 500, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#0a0a0a', marginTop: 4, fontVariantNumeric: 'tabular-nums', minHeight: 32 }}>
                {loading ? <Skeleton height={28} width="70%" /> : k.value}
              </div>
              <div style={{ fontSize: 11, fontWeight: 500, color: k.delta ? (k.delta.neutral ? '#52525b' : k.delta.up ? '#16a34a' : '#dc2626') : '#a1a1aa', marginTop: 3 }}>
                {loading ? <Skeleton height={11} width="40%" /> : (k.delta?.text || '')}
              </div>
            </div>
          ))}
        </div>

        {/* Two columns: Top Products + Subscription sidebar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, marginBottom: 24 }}>
          {/* Top Products — scoped to selected period, top 5 descending */}
          <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: '20px 24px' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0a0a0a', marginBottom: 16 }}>
              Top Products — {period === 'yesterday' ? 'yesterday' : period === '7d' ? 'last 7 days' : 'last 30 days'}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['Product', 'Units', 'Revenue', '% of top'].map(col => (
                    <th key={col} style={{ textAlign: col === 'Product' ? 'left' : 'right', fontSize: 10, fontWeight: 500, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 0 8px', borderBottom: '1px solid #f4f4f5' }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && [...Array(5)].map((_, i) => (
                  <tr key={i}><td colSpan={4} style={{ padding: '10px 0' }}><Skeleton height={16} /></td></tr>
                ))}
                {!loading && topProducts.map(pr => (
                  <tr key={`${pr.sku}-${pr.title}`}>
                    <td style={{ padding: '10px 0', borderBottom: '1px solid #fafafa' }}>
                      <div style={{ fontWeight: 500, color: '#0a0a0a' }}>{pr.title}</div>
                      <div style={{ fontSize: 10, color: '#a1a1aa', fontFamily: 'var(--font-mono)' }}>{pr.sku}</div>
                    </td>
                    <td style={{ padding: '10px 0', borderBottom: '1px solid #fafafa', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#52525b' }}>{Math.round(pr.units).toLocaleString()}</td>
                    <td style={{ padding: '10px 0', borderBottom: '1px solid #fafafa', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(pr.revenue, currency)}</td>
                    <td style={{ padding: '10px 0', borderBottom: '1px solid #fafafa', textAlign: 'right', color: '#52525b' }}>{topTotalRevenue > 0 ? `${((pr.revenue / topTotalRevenue) * 100).toFixed(1)}%` : '—'}</td>
                  </tr>
                ))}
                {!loading && topProducts.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: '20px 0', textAlign: 'center', color: '#a1a1aa', fontSize: 12 }}>No product data for this period</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Skio sidebar — subscription-specific KPIs for the selected period */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { label: 'Active Subscribers', value: loading ? null : fmtCount(skio?.active), color: '#7c3aed' },
              { label: 'Net New (period)', value: loading ? null : fmtSignedCount(skio?.net_new), color: (skio?.net_new || 0) >= 0 ? '#16a34a' : '#dc2626' },
              { label: 'Cancellations (period)', value: loading ? null : fmtCount(skio?.cancellations), color: '#dc2626', delta: cancDelta },
              { label: 'Churn Rate', value: loading ? null : fmtPct(skio?.churn_rate_pct), color: '#d97706' },
            ].map(s => (
              <div key={s.label} style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 500, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#0a0a0a', marginTop: 2, fontVariantNumeric: 'tabular-nums', minHeight: 28 }}>
                    {loading ? <Skeleton height={24} width="70%" /> : s.value}
                  </div>
                </div>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
              </div>
            ))}
          </div>
        </div>

        {/* VIP Orders — ranked and narrativised by Claude, refreshed 06:00 Sydney daily */}
        <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: '20px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0a0a0a' }}>VIP Orders</div>
              <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 2 }}>
                {(() => {
                  const enriched = (data?.vip_orders || []).some(v => v.enriched);
                  const enrichedAt = data?.vip_orders?.find(v => v.enriched_at)?.enriched_at;
                  if (enriched) {
                    return <>Ranked by Claude {enrichedAt && <>· last run {fmtRelativeTime(enrichedAt)}</>} · filtered to selected period</>;
                  }
                  return <>Rule-based (Claude re-rank runs daily 06:00 Sydney) · filtered to selected period</>;
                })()}
              </div>
            </div>
            {data?.vip_orders?.some(v => v.enriched) && (
              <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 999, background: '#ede9fe', color: '#7c3aed', fontWeight: 500, flexShrink: 0 }}>AI-ranked</span>
            )}
          </div>
          {loading && [...Array(4)].map((_, i) => (
            <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #fafafa' }}><Skeleton height={48} /></div>
          ))}
          {!loading && (() => {
            if (vips.length === 0) return (
              <div style={{ padding: '20px 0', textAlign: 'center', color: '#a1a1aa', fontSize: 12 }}>No VIP orders in this period</div>
            );
            return vips.map((o, i) => {
              const shortEmail = o.customer_email ? o.customer_email.split('@')[0] : '';
              const flag = FLAG_BY_COUNTRY[o.shipping_country] || '';
              const chips = vipSignalChips(o);
              const headline = o.narrative || `Order #${o.order_number}`;
              return (
                <div key={`${o.order_number}-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 0', borderBottom: i < vips.length - 1 ? '1px solid #fafafa' : 'none' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: `hsl(${((shortEmail.charCodeAt(0) || 65) * 37) % 360}, 45%, 65%)`, color: '#fff', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>{(shortEmail[0] || '?').toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a', lineHeight: 1.4 }}>{headline}</div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: '#71717a', fontFamily: 'var(--font-mono, monospace)' }}>#{o.order_number}</span>
                      <span style={{ fontSize: 10, color: '#a1a1aa' }}>·</span>
                      <span style={{ fontSize: 10, color: '#a1a1aa' }}>{o.customer_email || '(no email)'}</span>
                      {o.shipping_city && <><span style={{ fontSize: 10, color: '#a1a1aa' }}>·</span><span style={{ fontSize: 10, color: '#a1a1aa' }}>{o.shipping_city}</span></>}
                      {chips.map((c, ci) => (
                        <span key={ci} style={{ fontSize: 9, padding: '2px 7px', borderRadius: 999, background: c.bg, color: c.color, fontWeight: 600 }}>{c.label}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0a0a0a', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(o.total, o.currency)}</div>
                    {o.lifetime_orders > 0 && <div style={{ fontSize: 10, color: '#a1a1aa' }}>{o.lifetime_orders} order{o.lifetime_orders === 1 ? '' : 's'} · {fmtMoney(o.lifetime_spend, o.currency)} LTV</div>}
                  </div>
                  <div style={{ fontSize: 14, flexShrink: 0 }}>{flag}</div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}
